import { message } from 'antd';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { isQuickChat } from 'productLine';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { configOverview, ConfigOverviewParams } from 'services/api/admin';
import toLowerCase from 'utils/toLowerCase';

export interface useConfigOverviewProps {
  platform?: General.PlatformUpperCase;
  region?: General.UpperCaseRegion;
  regionList?: General.UpperCaseRegion[];
}

type Data = Partial<
  Record<General.PlatformUpperCase, Partial<Record<General.UpperCaseRegion, Aide.Overview>>>
>;

const lazadaNotSupportTasks: Aide.TaskName[] = ['NEGATIVE_REVIEW_SAVE', 'POSITIVE_REVIEW_CARE'];

const tiktokNotSupportTasks: Aide.TaskName[] = [
  'INVITE_FOLLOWER',
  'POSITIVE_REVIEW_CARE',
  'POSITIVE_REVIEW_REPLY',
  'NEGATIVE_REVIEW_SAVE',
  'NEGATIVE_REVIEW_REPLY',
];

const _aliexpressNotSupportTasks: Aide.TaskName[] = [
  'INVITE_FOLLOWER',
  'PRAISE_REMINDER',
  'NEGATIVE_REVIEW_REPLY',
  'NEGATIVE_REVIEW_SAVE',
  'POSITIVE_REVIEW_REPLY',
  'POSITIVE_REVIEW_CARE',
  'CANCEL_ORDER',
  'ONLY_RETUND_AND_REFUND',
  'CONFIRM_TRADE',
  'SHIPPED_REMINDER',
  'WAIT_FOR_TAX',
  'DELAY_SHIP',
  'WAIT_DELIVERY_AGAIN',
  'SYSTEM_CANCEL_ORDER',
];

const mercadoNotSupportTasks: Aide.TaskName[] = [
  'ORDER_REMINDER',
  'PAYMENT_REMINDER',
  'INVITE_FOLLOWER',
  'CANCEL_ORDER',
  'SYSTEM_CANCEL_ORDER',
  'POSITIVE_REVIEW_REPLY',
  'NEGATIVE_REVIEW_REPLY',
  'DELAY_SHIP',
  'DELIVERY_REMINDER',
  'WAIT_DELIVERY_AGAIN',
];

const kwaiSupportTasks: Aide.TaskName[] = [
  'PHRASE',
  'TIMEOUT_REPLY',
  'WELCOME_REPLY',
  'SCENARIO_STORE_GROUP',
  'NIGHT_WATCH',
];

const facebookSupportTasks: Aide.TaskName[] = ['PHRASE'];

const quickNotSupportTasks: Aide.TaskName[] = [
  'REMIND',
  'INVITE_FOLLOWER',
  'SCENARIO_STORE_GROUP',
  'NIGHT_WATCH',
];

const qaTasks: Aide.TaskName[] = ['SCENARIO_STORE_GROUP', 'NIGHT_WATCH'];

const deletedScenario: string[] = ['REVIEW_MANAGEMENT'];

const deletedTasks: string[] = [
  'CANCEL_TRADE_FOR_ADDRESS',
  'CANCEL_TRADE_FOR_INFO',
  'CANCEL_TRADE_FOR_VOUCHER',
  'ONLY_REFUND',
  'REFUND_GOODS_AND_REFUND',
  'RECEPTION_SCORE',
  'WAIT_FOR_TAX',
];

const needResetScenarioAllConfigMap: Partial<Record<Aide.ScenarioName, Aide.TaskName[]>> = {
  BASIC_RECEPTION: ['PHRASE', 'TIMEOUT_REPLY', 'WELCOME_REPLY', 'INVITE_FOLLOWER', 'REMIND'],
  REVIEW_MANAGEMENT_V2: [
    'PRAISE_REMINDER',
    'POSITIVE_REVIEW_CARE',
    'POSITIVE_REVIEW_REPLY',
    'NEGATIVE_REVIEW_SAVE',
    'NEGATIVE_REVIEW_REPLY',
  ],
  LOGISTICS_CARE: [
    'CONFIRM_TRADE',
    'DELAY_SHIP',
    'SHIPPED_REMINDER',
    'DELIVERY_REMINDER',
    'WAIT_DELIVERY_AGAIN',
    'SIGN_REMINDER',
  ],
  ORDER_ABANDON_REMINDER: ['ONLY_RETUND_AND_REFUND', 'CANCEL_ORDER', 'SYSTEM_CANCEL_ORDER'],
};

function useConfigOverview(props: useConfigOverviewProps) {
  const { platform, region, regionList } = props;

  const { t } = useTranslate();

  const { qaRegionList } = useFlagConfigValue();

  const { whiteListConfig: { AE_CONVERSATION = false } = {} } = useOrgConfigStateValue();

  const aliexpressNotSupportTasks = useMemo<Aide.TaskName[]>(
    () =>
      AE_CONVERSATION ? _aliexpressNotSupportTasks : [..._aliexpressNotSupportTasks, 'PHRASE'],
    [AE_CONVERSATION],
  );

  const [data, setData] = useState<Data>({});

  const [currentLoading, setCurrentLoading] = useState<ConfigOverviewParams>();

  const fetchFlagRef = useRef(0);

  const refsCurrent = {
    regionList,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (!platform || !region) {
      return () => {};
    }

    const currentFetchFlag = fetchFlagRef.current;

    const rollFetch = (params: ConfigOverviewParams): Promise<any> => {
      setCurrentLoading(params);

      return configOverview(params).then((res) => {
        if (currentFetchFlag !== fetchFlagRef.current) {
          return Promise.resolve();
        }

        const filter = (taskNames: Aide.TaskName[]) => {
          return taskNames.reduce<Aide.TaskName[]>((res, task) => {
            if (deletedTasks.includes(task)) {
              return res;
            }

            if (isQuickChat && quickNotSupportTasks.includes(task)) {
              return res;
            }

            if (params.platform === 'LAZADA' && lazadaNotSupportTasks.includes(task)) {
              return res;
            }

            if (isTiKTokUpperCase(params.platform) && tiktokNotSupportTasks.includes(task)) {
              return res;
            }

            if (params.platform === 'ALIEXPRESS' && aliexpressNotSupportTasks.includes(task)) {
              return res;
            }

            if (params.platform === 'MERCADO' && mercadoNotSupportTasks.includes(task)) {
              return res;
            }

            if (params.platform === 'KWAI' && !kwaiSupportTasks.includes(task)) {
              return res;
            }

            if (params.platform === 'FACEBOOK' && !facebookSupportTasks.includes(task)) {
              return res;
            }

            if (!qaRegionList.includes(toLowerCase(params.region)) && qaTasks.includes(task)) {
              return res;
            }

            res.push(task);

            return res;
          }, []);
        };

        const scenarioFitler = (scenario: Aide.Scenario) => {
          const { name } = scenario;

          return !deletedScenario.includes(name);
        };

        const scenarios = res.configItems.filter(scenarioFitler).map<Aide.Scenario>((scenario) => {
          const { name, allConfigs: originAllConfigs, enabledConfigs, ...others } = scenario;

          const allConfigs = needResetScenarioAllConfigMap[name] || originAllConfigs;

          return {
            ...others,
            name,
            allConfigs: filter(allConfigs),
            enabledConfigs: filter(enabledConfigs),
          };
        });

        const overview: Aide.Overview = {
          scenarios,
          totalTaskCount: scenarios.reduce((res, cur) => res + cur.allConfigs.length, 0),
          enabledTaskCount: scenarios.reduce((res, cur) => res + cur.enabledConfigs.length, 0),
        };

        let nextRegion;

        setData((pre) => {
          const regionMap = pre[params.platform] || {};

          const regionList = refs.current.regionList || [];

          nextRegion = regionList.find((region) => !regionMap[region] && region !== params.region);

          return {
            ...pre,
            [params.platform]: {
              ...regionMap,
              [params.region]: overview,
            },
          };
        });

        if (nextRegion) {
          return rollFetch({
            ...params,
            region: nextRegion,
          });
        }

        setCurrentLoading(undefined);

        return Promise.resolve();
      });
    };

    rollFetch({
      platform,
      region,
    }).catch(() => {
      if (currentFetchFlag !== fetchFlagRef.current) {
        return;
      }

      setCurrentLoading(undefined);

      message.error(refs.current.t('phrase.获取数据失败，请稍后再试'));
    });

    return () => {
      fetchFlagRef.current += 1;
    };
  }, [platform, region, qaRegionList, aliexpressNotSupportTasks]);

  return {
    data,
    currentLoading,
  };
}

export default useConfigOverview;
